import React, { useState, useEffect, useMemo } from 'react';
import { Col, FormGroup, Input, Row, Label, Button, Alert } from 'reactstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import Loading from '../../components/Loading/Loading';
import paginationFactory from 'react-bootstrap-table2-paginator';
import moment from 'moment';
import axios from 'axios';
import { STATUS_OPTIONS, NOTIFICATION_TYPE_OPTIONS } from './constants';

const MeterPointNotificationsManagement = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [completing, setCompleting] = useState(null);

  // Alert state
  const [alert, setAlert] = useState({ visible: false, message: '', color: 'success' });

  // Pagination state
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [totalSize, setTotalSize] = useState(0);
  const [sortBy, setSortBy] = useState('receivedOn');
  const [sortDir, setSortDir] = useState('desc');

  // Filter state
  const [filters, setFilters] = useState({
    completed: '',
    notificationType: '',
  });

  // Debounced search state
  const [mpanNotificationSearch, setMpanNotificationSearch] = useState('');
  const [debouncedMpanNotificationSearch, setDebouncedMpanNotificationSearch] = useState('');

  // Debounce logic for MPAN and Notification Ref search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMpanNotificationSearch(mpanNotificationSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [mpanNotificationSearch]);

  // Common header style for all columns
  const commonHeaderStyle = {
    whiteSpace: 'normal',
    lineHeight: '1.2',
  };

  // Common date formatter
  const formatDate = (cell) => (cell ? moment(cell).format('DD/MM/YYYY HH:mm') : '');

  // Common date sort value
  const getDateSortValue = (cell) => (cell ? moment(cell).valueOf() : 0);

  // Common option formatter factory
  const createOptionFormatter = (options) => (cell) => {
    const option = options.find((opt) => opt.value === cell);
    return option ? option.label : cell;
  };

  // Complete notification handler
  const handleCompleteNotification = async (notificationId) => {
    try {
      setCompleting(true);

      const token = localStorage.token;

      await axios.post(
        `/df/dataflows/rest/v1/notifications/complete`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          params: { id: notificationId },
        }
      );

      // Show success message
      setAlert({
        visible: true,
        message: 'Notification completed successfully!',
        color: 'success',
      });

      // Hide alert after 5 seconds
      setTimeout(() => {
        setAlert((prev) => ({ ...prev, visible: false }));
      }, 5000);

      // Reload table data after successful completion
      await fetchData();
    } catch (error) {
      // Error handling for notification completion
      let errorMessage = 'Failed to complete notification. ';

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        if (status === 401) {
          errorMessage += 'Authentication failed. Please log in again.';
        } else if (status === 403) {
          errorMessage += 'You do not have permission to complete this notification.';
        } else if (status === 404) {
          errorMessage += 'Notification not found.';
        } else if (status === 400) {
          errorMessage += data.message || 'Invalid request.';
        } else if (status >= 500) {
          errorMessage += 'Server error. Please try again later.';
        } else {
          errorMessage += data.message || `Error ${status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Network error
        errorMessage += 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage += error.message || 'An unexpected error occurred.';
      }

      // Show error message
      setAlert({
        visible: true,
        message: errorMessage,
        color: 'danger',
      });

      // Hide alert after 8 seconds for errors (longer to read)
      setTimeout(() => {
        setAlert((prev) => ({ ...prev, visible: false }));
      }, 8000);
    } finally {
      setCompleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        dataField: 'notificationType',
        text: 'Notification Type',
        sort: true,
        headerStyle: commonHeaderStyle,
        formatter: createOptionFormatter(NOTIFICATION_TYPE_OPTIONS),
      },
      {
        dataField: 'receivedOn',
        text: 'Received On',
        headerStyle: commonHeaderStyle,
        formatter: formatDate,
        sort: true,
        sortValue: getDateSortValue,
      },
      {
        dataField: 'mpanCore',
        text: 'MPAN',
        headerStyle: commonHeaderStyle,
        sort: true,
      },
      {
        dataField: 'notificationRef',
        text: 'Ref',
        headerStyle: commonHeaderStyle,
        sort: true,
      },
      {
        dataField: 'detail',
        text: 'Details',
        headerStyle: commonHeaderStyle,
        formatter: (cell) => {
          try {
            return (
              <pre
                style={{
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  maxHeight: '100px',
                  textAlign: 'left',
                  fontSize: '12px',
                  margin: 0,
                }}
              >
                {JSON.stringify(cell, null, 2)}
              </pre>
            );
          } catch (e) {
            return cell;
          }
        },
      },
      {
        dataField: 'completed',
        text: 'Completed',
        headerStyle: commonHeaderStyle,
        sort: true,
        formatter: (cell) => (cell ? 'Yes' : 'No'),
      },
      {
        dataField: 'completedOn',
        text: 'Completed On',
        headerStyle: commonHeaderStyle,
        formatter: formatDate,
        sort: true,
        sortValue: getDateSortValue,
      },
      {
        dataField: 'actions',
        text: 'Actions',
        headerStyle: commonHeaderStyle,
        isDummyField: true,
        formatter: (cell, row) => {
          if (!row.completed) {
            return (
              <Button
                color='primary'
                size='sm'
                onClick={() => handleCompleteNotification(row.id)}
                disabled={completing}
              >
                {completing ? 'Processing...' : 'Complete'}
              </Button>
            );
          }
          return null;
        },
      },
    ],
    [NOTIFICATION_TYPE_OPTIONS, completing]
  );

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);

    try {
      const token = localStorage.token;

      // Build query parameters
      const params = {
        page: page - 1,
        size: size,
        sortBy: sortBy,
        sortDir: sortDir,
      };

      // Add filters only if they have values
      Object.keys(filters).forEach((key) => {
        if (filters[key] && filters[key].trim() !== '') {
          params[key] = filters[key];
        }
      });

      // Add debounced search parameters if they have values
      if (debouncedMpanNotificationSearch && debouncedMpanNotificationSearch.trim() !== '') {
        params.searchValue = debouncedMpanNotificationSearch;
      }

      const response = await axios.get('/df/dataflows/rest/v1/notifications', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTableData(response.data.content || []);
      setTotalSize(response.data.totalElements || 0);
    } catch (error) {
      // Error handling for data fetching
      setTableData([]);
      setTotalSize(0);
    }
    setLoading(false);
  };

  // Fetch data when page/size/sort/filters change
  useEffect(() => {
    fetchData();
  }, [page, size, sortBy, sortDir, filters, debouncedMpanNotificationSearch]);

  // Table change handler
  const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === 'sort') {
      setSortBy(sortField);
      setSortDir(sortOrder);
    }
    if (type === 'pagination') {
      setPage(page);
      setSize(sizePerPage);
    }
  };

  // Filter change handler
  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
    setPage(1); // Reset to first page on filter change
  };

  // Render select filter helper
  const renderSelectFilter = (id, label, value, options) => (
    <FormGroup>
      <Label for={id}>{label}</Label>
      <Input type='select' id={id} value={value} onChange={(e) => handleFilterChange(id, e.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
    </FormGroup>
  );

  return (
    <div className='organization'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Meter Point Notifications Management</h1>
        </Col>
      </Row>

      <div className='page-cnt'>
        {/* Alert Section */}
        {alert.visible && (
          <Row className='mb-3'>
            <Col>
              <Alert color={alert.color} toggle={() => setAlert((prev) => ({ ...prev, visible: false }))}>
                {alert.message}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Filters Section */}
        <Row className='mb-4'>
          <Col md={3}>{renderSelectFilter('completed', 'Status', filters.completed, STATUS_OPTIONS)}</Col>

          <Col md={3}>
            {renderSelectFilter(
              'notificationType',
              'Notification Type',
              filters.notificationType,
              NOTIFICATION_TYPE_OPTIONS
            )}
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label for='mpanNotificationSearch'>MPAN / Notification Ref</Label>
              <Input
                type='text'
                id='mpanNotificationSearch'
                value={mpanNotificationSearch}
                onChange={(e) => setMpanNotificationSearch(e.target.value)}
                placeholder='Search MPAN or Notification Ref'
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col className='col-12'>
            {loading && <Loading />}
            <BootstrapTable
              classes='table-hover'
              wrapperClasses='table-responsive'
              noDataIndication={() => <div>No records in table.</div>}
              columns={columns}
              data={tableData}
              keyField={'id'}
              bordered={false}
              hover
              remote={{
                filter: false,
                pagination: true,
                sort: true,
              }}
              pagination={paginationFactory({
                page: page,
                sizePerPage: size,
                totalSize: totalSize,
                sizePerPageList: [
                  { text: '5', value: 5 },
                  { text: '10', value: 10 },
                  { text: '20', value: 20 },
                  { text: '50', value: 50 },
                ],
              })}
              onTableChange={handleTableChange}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default MeterPointNotificationsManagement;
